import { useNavigate } from "react-router-dom";
import { loginPath } from "./auth";
import { MethodologyName } from "../api/examinationApi";

// request paths
export const archiveDocumentationsURL = "/api/users/archives/documents/";
export const archiveExaminationsURL = "/api/users/archives/examinations/";
export const changePasswordURL = "/api/users/password_change/";
export const changePasswordExpiredURL = "/api/users/password_change_expired/";
export const changePinURL = "/api/users/pin_change/";
export const directoriesURL = "/api/users/directories/";
export const drivingLicenseURL = "/api/users/driving-license-categories/";
export const examinationURL = "/api/core/examinations/";
export const examinationMultiURL = "/api/core/examinations/create-multi/";
export const examineesURL = "/api/users/examinees/";
export const getPsychologicalLabInfoURL = "/api/users/psych-lab-info/";
export const getResetPasswordEmailURL = "/api/users/password_reset/";
export const legalBasisURL = "/api/core/legal_bases/";
export const licensedMethodologiesURL = "/api/core/licensed_methodologies/";
export const moveExamineesURL = "/api/users/examinees/relocate-examinees/";
export const psychologicalTestsURL = "/api/core/tests/";
export const resetPasswordSendRequestURL = "/api/users/password_reset/confirm/";
export const getContractURL = "/api/users/contract/";
export const normSetURL = "/api/core/norms/";
export const psychologicalLabsGrantedURL = "/api/users/admin_labs_granted/";
export const psychologicalLabsURL = "/api/users/psychlabs/";
export const psychologicalLabsUserURL = "/api/users/psychlabs_users/";
export const psychologicalLabsActivityURL = "/api/users/psychlabs_activity/";
export const psychologicalLabsLicenceURL = "/api/users/psychlabs_license/";
export const psychologicalUserURL = "/api/users/psych_user/";
export const exportResultsURL = "/api/users/export_results/";
export const sendEmailURL = "/api/users/send_email/";
export const psychologicalMethodologyTestCategories = "/api/users/methodology/";
export const psychologicalTestTypes = "/api/users/test_types/";

// gatsby pages' paths
export const archiveExaminationPath = "/app/archive-examination";
export const addExamineePath = "/app/add-examinee";
export const importExamineesPath = "/app/import-examinees";
export const addPsychLabPath = "/app/add-psychlab";
export const addPsychLabUserPath = "/app/add-psychuser";
export const dashboardPath = "/app/dashboard";
export const dashboardAdminPath = "/app/admin-dashboard";
export const diagnosticReportPath = "/app/documentation/diagnostic-report";
export const directoriesPath = "/app/directories";
export const editExamineePath = "/app/edit-examinee";
export const examinationPath = "/app/examination";
export const examinationAssignmentPath = "/app/examination-assignment";
export const examineePath = "/app/examinee";
export const psychLabPath = "/app/psychlab";
export const editPsychUserPath = "/app/edit-psychuser/";
export const moveAssignExamsAndFoldersPath =
  "/app/move-assign-exams-and-folders/";
export const expiredPasswordPath = "/expiredPassword";
export const helpdeskPath = "/app/helpdesk";
export const cookiesPolicyPath = "/cookiespolicy/";
export const helpdeskDocumentPath = "/app/helpdesk/documents";
export const interpretationReportPath =
  "/app/documentation/interpretation-report";
export const resetPasswordGetEmailPath = "/getResetPasswordEmail";
export const searchPath = "/app/search";
export const searchAdminPath = "/app/admin-search";
export const userSettingsPath = "/app/user-settings";
export const adminSettingsPath = "/app/admin-settings";
export const adminExportResultsPath = "/app/admin-export";
export const adminSummaryPath = "/app/admin-summary";
export const adminPriceListPath = "/app/admin-pricelist";
export const adminEmailSenderPath = "/app/email-sender";
export const userExportResultsPath = "/app/user-export";
export const userImportExamineesPath = "/app/import-examinees";
export const remoteHelpPath =
  "https://anydesk.com/pl/downloads/windows?dv=win_exe";

export const useNavigation = () => {
  let navigate: any;

  // Sprawdź czy jesteśmy w przeglądarce
  if (typeof window === "undefined") {
    navigate = () => {
      // SSR - nie wykonuj nawigacji
    };
  } else {
    // Zawsze wywołuj useNavigate, ale obsłuż błędy
    try {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      navigate = useNavigate();
    } catch {
      // Jeśli useNavigate nie jest dostępne (poza kontekstem Routera)
      navigate = (path: string) => {
        if (typeof window !== "undefined") {
          window.location.href = path;
        }
      };
    }
  }

  const handleNavigateToPreviousPage = () => {
    if (typeof navigate === "function") {
      navigate(-1);
    } else if (typeof window !== "undefined") {
      window.history.back();
    }
  };

  const handleReloadPage = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  let isNavigating = false;

  const handleNavigateToPage = (path: string, newWindow = false) => {
    if (typeof window !== "undefined") {
      if (newWindow) {
        window.open(path, "_blank", "noreferrer");
      } else {
        isNavigating = true;

        if (
          typeof navigate === "function" &&
          (window.location.pathname.startsWith("/app") ||
            path.startsWith("/app"))
        ) {
          navigate(path);
        } else {
          window.location.href = path;
        }
        setTimeout(() => {
          isNavigating = false;
        }, 100);
      }
    }
  };

  const setupRefreshHandler = (handleLogout: () => void) => {
    if (typeof window !== "undefined") {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!isNavigating) {
          handleLogout();
          event.preventDefault();
          event.returnValue =
            "Czy na pewno chcesz odświeżyć stronę? Zostaniesz wylogowany.";
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }

    // Return empty cleanup function for SSR
    return () => {};
  };

  const handleNavigateExpiredPasswordPage = (userId: string) => {
    if (typeof navigate === "function") {
      navigate(`${expiredPasswordPath}/?userId=${userId}`);
    } else if (typeof window !== "undefined") {
      // Używamy replace zamiast href dla lepszej kompatybilności z Gatsby
      const timestamp = Date.now();

      window.location.replace(
        `${expiredPasswordPath}/?userId=${userId}&t=${timestamp}`
      );
    }
  };

  const handleNavigateToResetPage = () => {
    if (typeof navigate === "function") {
      navigate(resetPasswordGetEmailPath);
    } else if (typeof window !== "undefined") {
      window.location.href = resetPasswordGetEmailPath;
    }
  };

  const handleNavigateToLogin = () => {
    if (typeof window !== "undefined") {
      // Jeśli jesteśmy na stronie /app/* lub nawigujemy do /app/*
      if (
        typeof navigate === "function" &&
        (window.location.pathname.startsWith("/app") ||
          loginPath.startsWith("/app"))
      ) {
        navigate(loginPath);
      } else {
        window.location.href = loginPath;
      }
    }
  };

  const handleNavigateToReport = (
    examineeId: number,
    examinationId: string,
    reportType: "diagnostic" | "diagnostic-charts" | "interpretation"
  ) => {
    const path = `${examineePath}/${examineeId}/examination/${examinationId}/documentation/${reportType}-report/`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToDirectoryContent = (directoryId: number) => {
    const path = `${directoriesPath}/${directoryId}/`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToExamination = (
    examineeId: number,
    examinationId: string
  ) => {
    const path = `${examineePath}/${examineeId}/examination/${examinationId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToExaminationAssignment = (examineeId: number) => {
    const path = `${examinationAssignmentPath}/${examineeId}/`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToImportExaminationAssignment = () => {
    const path = `${examinationAssignmentPath}/import`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToPsychologicalTestsStep = (examineeId: number) => {
    const path = `${examinationAssignmentPath}/${examineeId}/tests-assignment`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToPsychologicalTestsStepExtendExam = (
    examineeId: number,
    examinationId: string
  ) => {
    const path = `${examinationAssignmentPath}/${examineeId}/${examinationId}/tests-assignment-extend`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToImportPsychologicalTestsStep = () => {
    const path = `${examinationAssignmentPath}/tests-assignment-import`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToAddExamineeDirectories = (): void => {
    const path = `${directoriesPath}/add-examinee`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToAddExamineeDirectory = (directory?: number): void => {
    if (directory) {
      const path = `${directoriesPath}/${directory}/add-examinee`;

      if (typeof navigate === "function") {
        navigate(path);
      } else if (typeof window !== "undefined") {
        window.location.href = path;
      }
    }
  };

  const handleNavigateToEditExamineeDirectory = (
    examineeId: number,
    directory?: number
  ) => {
    if (directory) {
      const path = `${directoriesPath}/${directory}/edit-examinee/${examineeId}`;

      if (typeof navigate === "function") {
        navigate(path);
      } else if (typeof window !== "undefined") {
        window.location.href = path;
      }
    }
  };

  const handleNavigateToEditPsychLab = (psychLabId: number) => {
    const path = `${psychLabPath}/edit-psychlab/${psychLabId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToAddPsychLabLic = (psychLabId: number) => {
    const path = `${psychLabPath}/add-psychlablic/${psychLabId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToEditPsychLabLic = (licenseId: number) => {
    const path = `${psychLabPath}/edit-psychlablic/${licenseId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToEditPsychUser = (psychUserId: number) => {
    const path = `${editPsychUserPath}${psychUserId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToExamineeProfile = (examineeId: number) => {
    if (examineeId) {
      const path = `${examineePath}/${examineeId}`;

      if (typeof navigate === "function") {
        navigate(path);
      } else if (typeof window !== "undefined") {
        window.location.href = path;
      }
    }
  };

  const handleNavigateToEditExamineeProfile = (examineeId: number) => {
    if (examineeId) {
      const path = `${examineePath}/${examineeId}/edit-examinee`;

      if (typeof navigate === "function") {
        navigate(path);
      } else if (typeof window !== "undefined") {
        window.location.href = path;
      }
    }
  };

  const handleNavigateToPsychLabProfile = (id: number) => {
    const path = `${psychLabPath}/${id}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToDisplayArchiveExamination = (
    archiveExaminationId: string,
    examineeId: number,
    legacyId: string
  ) => {
    const path = `${archiveExaminationPath}/${archiveExaminationId}/${examineeId}/${legacyId}`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToDocument = (
    examineeId: number,
    examinationId: string,
    documentType: string,
    documentId: number,
    methodology?: MethodologyName
  ) => {
    let path;

    if (documentType === "examination-card") {
      // Dla examination-card kolejność to: documentId, methodology
      const methodologyPath = methodology ? `/${methodology}` : "";

      path = `${examineePath}/${examineeId}/examination/${examinationId}/documentation/${documentType}/${documentId}${methodologyPath}`;
    } else {
      // Dla innych dokumentów kolejność to: methodology, documentId
      const methodologyPath = methodology ? `/${methodology}` : "";

      path = `${examineePath}/${examineeId}/examination/${examinationId}/documentation/${documentType}${methodologyPath}/${documentId}`;
    }

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  const handleNavigateToInterview = (
    examineeId: number,
    examinationId: string
  ) => {
    const path = `${examineePath}/${examineeId}/examination/${examinationId}/interview`;

    if (typeof navigate === "function") {
      navigate(path);
    } else if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  return {
    handleNavigateToPreviousPage,
    handleReloadPage,
    handleNavigateToPage,
    setupRefreshHandler,
    handleNavigateExpiredPasswordPage,
    handleNavigateToResetPage,
    handleNavigateToLogin,
    handleNavigateToReport,
    handleNavigateToDirectoryContent,
    handleNavigateToExamination,
    handleNavigateToExaminationAssignment,
    handleNavigateToImportExaminationAssignment,
    handleNavigateToPsychologicalTestsStep,
    handleNavigateToPsychologicalTestsStepExtendExam,
    handleNavigateToImportPsychologicalTestsStep,
    handleNavigateToAddExamineeDirectories,
    handleNavigateToAddExamineeDirectory,
    handleNavigateToEditExamineeDirectory,
    handleNavigateToEditPsychLab,
    handleNavigateToAddPsychLabLic,
    handleNavigateToEditPsychLabLic,
    handleNavigateToEditPsychUser,
    handleNavigateToExamineeProfile,
    handleNavigateToEditExamineeProfile,
    handleNavigateToPsychLabProfile,
    handleNavigateToDisplayArchiveExamination,
    handleNavigateToDocument,
    handleNavigateToInterview,
  };
};